type BroadcastEventType = 'customer-changed' | 'logout';

interface BroadcastMessage {
  type: BroadcastEventType;
  payload?: any;
  timestamp: number;
  tabId: string;
}

type EventCallback = (payload: any) => void;

class BroadcastService {
  private channel: BroadcastChannel | null = null;
  private listeners: Map<BroadcastEventType, Set<EventCallback>> = new Map();
  private tabId: string;

  constructor() {
    // Generate unique tab ID
    this.tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Initialize BroadcastChannel if supported
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel('inocu_app_channel');
      this.setupMessageListener();
    } else {
      console.warn('BroadcastChannel API not supported in this browser');
    }
  }

  /**
   * Setup listener for incoming broadcast messages
   */
  private setupMessageListener(): void {
    if (!this.channel) return;

    this.channel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
      const message = event.data;

      // Ignore messages from the same tab
      if (message.tabId === this.tabId) {
        return;
      }

      // Execute registered callbacks for this event type
      const callbacks = this.listeners.get(message.type);
      if (callbacks) {
        callbacks.forEach(callback => {
          try {
            callback(message.payload);
          } catch (error) {
            console.error(`Error executing broadcast callback for ${message.type}:`, error);
          }
        });
      }
    };
  }

  /**
   * Broadcast a message to all other tabs
   * @param type Event type
   * @param payload Optional event data
   */
  emit(type: BroadcastEventType, payload?: any): void {
    if (!this.channel) {
      console.warn('BroadcastChannel not available, cannot emit event');
      return;
    }

    const message: BroadcastMessage = {
      type,
      payload,
      timestamp: Date.now(),
      tabId: this.tabId,
    };

    this.channel.postMessage(message);
  }

  /**
   * Register a callback for a specific event type
   * @param type Event type to listen for
   * @param callback Function to call when event is received
   * @returns Unsubscribe function
   */
  on(type: BroadcastEventType, callback: EventCallback): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }

    this.listeners.get(type)!.add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(type);
      if (callbacks) {
        callbacks.delete(callback);
      }
    };
  }

  /**
   * Remove a specific callback for an event type
   * @param type Event type
   * @param callback Callback to remove
   */
  off(type: BroadcastEventType, callback: EventCallback): void {
    const callbacks = this.listeners.get(type);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * Remove all listeners for a specific event type
   * @param type Event type
   */
  removeAllListeners(type?: BroadcastEventType): void {
    if (type) {
      this.listeners.delete(type);
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Broadcast customer change event to other tabs
   * Other tabs will reload to reflect the new customer context
   * @param customerId New customer ID
   */
  notifyCustomerChanged(customerId: number): void {
    this.emit('customer-changed', { customerId });
  }

  /**
   * Listen for customer change events from other tabs
   * @param callback Function to call when customer changes in another tab
   * @returns Unsubscribe function
   */
  onCustomerChanged(callback: (customerId: number) => void): () => void {
    return this.on('customer-changed', payload => {
      if (payload && payload.customerId) {
        callback(payload.customerId);
      }
    });
  }

  /**
   * Broadcast logout event to other tabs
   */
  notifyLogout(): void {
    this.emit('logout');
  }

  /**
   * Listen for logout events from other tabs
   * @param callback Function to call when logout occurs in another tab
   * @returns Unsubscribe function
   */
  onLogout(callback: () => void): () => void {
    return this.on('logout', callback);
  }

  /**
   * Close the broadcast channel
   * Should be called when the tab is being closed or the service is no longer needed
   */
  close(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.listeners.clear();
  }

  /**
   * Get the unique ID for this tab
   */
  getTabId(): string {
    return this.tabId;
  }
}

export default new BroadcastService();
